import { type Partner } from '@mzc-watchover/adminapp-appsync'
import { compact, head, isNil, join, map, split } from 'lodash/fp'
import {
  ArrayInput,
  email,
  Form,
  SimpleFormIterator,
  TextInput,
  useNotify,
  useTranslate
} from 'react-admin'
import { Button, Card, CardContent, Grid, Typography } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { updatePartner } from 'components/DataProvider/functions'
import { useGetPartner } from 'components/DataProvider/hooks'

export const ContactEdit: React.FC<{
  partner: Partial<Partner> | undefined
}> = ({ partner: partnerArg }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const { data: partner, isLoading } = useGetPartner(
    isNil(partnerArg) ? undefined : partnerArg.id
  )
  const { id, products } = partner ?? { products: [] }
  const { contactEmail, contactOrganization } = head(products) ?? {
    contactOrganization: null,
    contactEmail: null
  }
  if (isNil(contactEmail) || isNil(contactOrganization) || isNil(id)) {
    return null
  }
  if (isLoading || isNil(partner)) return null
  const editEmails = map((email: string) => ({ email }))(
    split(',')(contactEmail)
  )
  const transform =
    (id: string, editEmails: Array<{ email: string }>) => () => {
      const contactEmail = join(',')(
        compact(map((item: { email: string }) => item.email)(editEmails))
      )
      updatePartner(id, contactOrganization, contactEmail)
        .then((status) => {
          if (status === 200) {
            notify('resources.dashboard.notification.submit')
          } else {
            notify('resources.dashboard.notification.submitFail')
          }
        })
        .catch(() => {})
    }
  return (
    <Card raised sx={{ width: '80%', marginBottom: '1em' }}>
      <CardContent>
        <Form
          noValidate
          mode="onChange"
          reValidateMode="onChange"
          defaultValues={{
            editEmails
          }}>
          <Grid container>
            <Grid xs={12} sx={{ mb: '0.5em' }}>
              <Typography variant="h6">
                {translate('resources.contactEdit.title')}
              </Typography>
            </Grid>
            <Grid xs={12}>
              <ArrayInput label={false} source="editEmails">
                <SimpleFormIterator disableClear disableReordering fullWidth>
                  <TextInput
                    type="email"
                    source="email"
                    fullWidth
                    label={false}
                    validate={email()}
                  />
                </SimpleFormIterator>
              </ArrayInput>
            </Grid>
          </Grid>
          <SubmitButton id={id} onSubmit={transform} />
        </Form>
      </CardContent>
    </Card>
  )
}

const SubmitButton: React.FC<{
  id: string
  onSubmit: (id: string, data: Array<{ email: string }>) => () => void
}> = ({ id, onSubmit }) => {
  const translate = useTranslate()
  const { getValues } = useFormContext()
  const { editEmails } = getValues()
  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      sx={{ borderRadius: '10px' }}
      onClick={onSubmit(id, editEmails)}>
      <Typography variant="button">
        {translate('resources.contactEdit.buttons.save')}
      </Typography>
    </Button>
  )
}
